/** The en-GB strings, one namespace for each file of `locales/en-GB/` (`25` TON-032). */
import admin from '../locales/en-GB/admin.json' with { type: 'json' };
import ai from '../locales/en-GB/ai.json' with { type: 'json' };
import atlas from '../locales/en-GB/atlas.json' with { type: 'json' };
import biography from '../locales/en-GB/biography.json' with { type: 'json' };
import career from '../locales/en-GB/career.json' with { type: 'json' };
import create from '../locales/en-GB/create.json' with { type: 'json' };
import economy from '../locales/en-GB/economy.json' with { type: 'json' };
import first_minute from '../locales/en-GB/first_minute.json' with { type: 'json' };
import golfer_screen from '../locales/en-GB/golfer_screen.json' with { type: 'json' };
import glossary from '../locales/en-GB/glossary.json' with { type: 'json' };
import habit from '../locales/en-GB/habit.json' with { type: 'json' };
import help from '../locales/en-GB/help.json' with { type: 'json' };
import holiday from '../locales/en-GB/holiday.json' with { type: 'json' };
import names from '../locales/en-GB/names.json' with { type: 'json' };
import reason from '../locales/en-GB/reason.json' with { type: 'json' };
import round from '../locales/en-GB/round.json' with { type: 'json' };
import season from '../locales/en-GB/season.json' with { type: 'json' };
import shell from '../locales/en-GB/shell.json' with { type: 'json' };
import skills from '../locales/en-GB/skills.json' with { type: 'json' };
import study from '../locales/en-GB/study.json' with { type: 'json' };
import time from '../locales/en-GB/time.json' with { type: 'json' };
import today from '../locales/en-GB/today.json' with { type: 'json' };
import training from '../locales/en-GB/training.json' with { type: 'json' };
import ui from '../locales/en-GB/ui.json' with { type: 'json' };
import units from '../locales/en-GB/units.json' with { type: 'json' };

/**
 * `glossary`: the glossary's rows, one plain sentence for each term (`25` TON-028, TON-036 to
 * TON-038, TON-083). The rows are content now; the glossary page that lists them is `[V1]`.
 * `skills`: the skill and area names and the words of the plain label (`08` GOL-065, GOL-066; `25`).
 * `today` and `golfer_screen`: the words of Today (`23` HOME-01) and Your golfer (GOLF-01, SOC-02).
 * `economy`: the wallet's words and the plain name of each reason code a player's statement shows (`23` ECO-01;
 * `17` ECO-150).
 * `career`: the player-facing name of each stage of the career ladder, keyed by its stage code (`13` CAR-001;
 * `02` §3). College golf is in the ladder and switched off (CAR-210).
 * `training`: the name of each training activity (the Activity column of `09` TRN-020's table; travel's two
 * rows without their distance rule) and the workload gauge's words (`09` §2).
 * `holiday`: holiday mode's booking sheet, the holiday card and its words (`25` TON-077; `20` ENG-181).
 * `study`: the study session's words (`25` TON-094 table (f), its Stage 4 rows; `09` TRN-200). The Course study
 * screen's own rows ship with the screen (`23` SCR-076, Stage 10).
 * `season`: the season banner on Today and the season calendar (`23` HOME-01 point (a), HOME-06; `06` CLK-010 to
 * CLK-018; the names of the majors and the cups are `02` §3's).
 * `atlas`: the Course Atlas's words: the hazard names, the text-only hole summary and the "to come" line of a hole
 * with no picture yet (`07` CRS-043, CRS-105; `24` DES-576; `38` VW-232).
 * `help`: How the game works, the first page of the help centre: what the engine plays and what it keeps hidden
 * (`23` SYS-05; `10` SIM-001, SIM-313, SIM-314; ruling H6). Every claim is true of the engine as built.
 */
export const NAMESPACES = [
  'shell',
  'units',
  'time',
  'round',
  'reason',
  'ui',
  'admin',
  'glossary',
  'habit',
  'skills',
  'names',
  'biography',
  'ai',
  'create',
  'first_minute',
  'today',
  'golfer_screen',
  'career',
  'economy',
  'training',
  'holiday',
  'study',
  'season',
  'atlas',
  'help',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const EN_GB = {
  shell,
  units,
  time,
  round,
  reason,
  ui,
  admin,
  glossary,
  habit,
  skills,
  names,
  biography,
  ai,
  create,
  first_minute,
  today,
  golfer_screen,
  career,
  economy,
  training,
  holiday,
  study,
  season,
  atlas,
  help,
} as const satisfies Record<Namespace, object>;

/**
 * Every key of a namespace's nested JSON, as "a.b.c". A `$` block (the bank fields of `$banks`,
 * `21` AI-240) holds no player string and is left out.
 */
export function keysOf(tree: object, prefix = ''): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(tree)) {
    if (k.startsWith('$')) continue;
    const key = prefix === '' ? k : prefix + '.' + k;
    if (typeof v === 'string') out.push(key);
    else if (v !== null && typeof v === 'object') out.push(...keysOf(v as object, key));
  }
  return out;
}
